import { Button } from '@/components';
import IconButton from '@/components/IconButton';
import SettingsModal from '@/components/SettingsModal';
import { RefreshIcon } from '@/components/icons';
import { Link, links as _links } from '@/utils/links';
import { loadState, removeState, saveState } from '@/utils/localStorage';
import DateRangePicker from '@components/DateRangePicker';
import HamburgerMenu from '@components/HamburgerMenu';
import SelectYears from '@components/SelectYears';
import StravaLogo from '@components/StravaLogo';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { useSessionId } from '../cookies';
import { useActivities } from '../hooks/useActivities';
import { useAnalyticsEventTracker } from '../hooks/useAnalyticsEventTracker';
import { authState } from '../recoil';
import styles from './appbar.module.css';

const localStorageKey = 'userSettings';
const localStorageKeyLinks = 'links';

export type UserSettings = {
  showDateRangePicker: boolean;
  darkMode: boolean;
  isMobile: boolean;
  weight: number;
  FTP: number;
};

export type UserSettingsKey = keyof UserSettings;

const defaultUserSettings: UserSettings = {
  showDateRangePicker: false,
  darkMode: false,
  isMobile: false,
  weight: 75,
  FTP: 290,
};

const Appbar = () => {
  const { removeSessionId } = useSessionId();
  const navigate = useNavigate();
  const [openHeader, setOpenHeader] = useState(false);
  const storedUserSettings = loadState(localStorageKey);
  const storedLinks = loadState(localStorageKeyLinks);
  const [userSettings, setUserSettings] = useState<typeof defaultUserSettings>(
    storedUserSettings || defaultUserSettings
  );
  const [links, setLinks] = useState<Link[]>(storedLinks || _links);
  const [value, setValue] = useState('');
  const [_authorizationCode, setAuthorizationCode] = useRecoilState(authState);
  const { refetch, dataUpdatedAt, isFetching } = useActivities();
  // Google Analytics event tracker
  const gaEventTracker = useAnalyticsEventTracker('appBar');

  const toggleUserSettings = (key: UserSettingsKey) => {
    setUserSettings((prevState) => {
      const newUserSettings = {
        ...prevState,
        [key]: !prevState[key],
      };
      saveState(localStorageKey, newUserSettings);
      return newUserSettings;
    });
  };

  const toggleLink = (path: string) => {
    setLinks((prevState) => {
      const newLinks = prevState.map((link) => {
        if (link.path === path) {
          return {
            ...link,
            show: !link.show,
          };
        }
        return link;
      });
      saveState(localStorageKeyLinks, newLinks);
      return newLinks;
    });
  };

  const handleChangeUserSettings = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    name: UserSettingsKey
  ) => {
    const { value } = event.target;
    setUserSettings((prevState) => {
      const newUserSettings = {
        ...prevState,
        [name]: value,
      };
      saveState(localStorageKey, newUserSettings);
      return newUserSettings;
    });
  };

  const formattedDataUpdated = new Date(dataUpdatedAt).toLocaleString();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  const handleLogoutClick = () => {
    removeSessionId();
    setAuthorizationCode(null);
    navigate('/oauth');
    localStorage.clear();
    gaEventTracker('logout');
    removeState(localStorageKey);
    removeState(localStorageKeyLinks);
  };

  const handleRefreshClick = () => {
    refetch();
  };

  useEffect(() => {
    setOpenHeader(true);
  }, []);

  const location = useLocation();
  const path = location.pathname;

  const ranges = value?.toString().split(', ');

  return (
    <header className={clsx(styles.header, openHeader && styles.open)}>
      <div className={styles.branding}>
        <StravaLogo />
        <SelectYears refetch={refetch} />
        {userSettings.showDateRangePicker && (
          <DateRangePicker value={value} handleChange={handleChange} />
        )}
      </div>
      <nav className={styles.nav}>
        <HamburgerMenu className={styles.hamburger} />
        {links.map((link) => {
          const Icon = link.icon;
          return (
            link.show && (
              <RouterLink
                key={link.path}
                to={link.path}
                data-active={path === `/home/${link.path}`}
              >
                {Icon && <Icon style={{ marginRight: '2px' }} />}
                {link.name}
                {link.experimental ? '⚠️ ' : ''}
              </RouterLink>
            )
          );
        })}
      </nav>
      <div className={styles.headerButtonContainer}>
        <Typography
          variant="body2"
          color={'GrayText'}
        >{`(last updated: ${formattedDataUpdated})`}</Typography>
        <IconButton onClick={handleRefreshClick}>
          <RefreshIcon className={clsx(isFetching && styles.loading)} />
        </IconButton>
        <SettingsModal
          userSettings={userSettings}
          toggleCheckbox={toggleUserSettings}
          toggleLink={toggleLink}
          links={links}
          handleChange={handleChangeUserSettings}
        />
        <Button className={styles.headerButton} onClick={handleLogoutClick}>
          Logout
        </Button>
      </div>
    </header>
  );
};

export default Appbar;
